/**
 * prebilling — the client record's Pre-billing tab client (native
 * billing v0).
 *
 * ONE fetch per (client, month) view:
 *   GET /api/t/{slug}/v1/clients/{clientId}/prebilling?month=YYYY-MM
 *     → { month, native_from?, rows: PrebillingRowWire[] }
 *
 * A seat without view_invoice receives the care-manager payload
 * (api/_shared/prebillingProjection.ts): no native_from, and no status,
 * author_display_name or billing_ready_at on a row. Those keys are
 * optional here, and a missing one reads as null.
 *
 * The server derives each posted note's billing-readiness state
 * (repos/encounterNotes.ts deriveBillingReadiness — GC-ruled states);
 * this module is transport + the react-query hook (clientRecordApi.ts
 * house pattern) + the pure display/export helpers the tab and its
 * tests share.
 */

import { writeFetch } from './writeFetch';
import { useQuery } from '@tanstack/react-query';
import { ApiError } from './api';
import { isPerVisitBand, type RateBandOption } from './notes';
import { saysNoCharge } from './invoiceLinePresence';
import { MONTHS, proseDate } from './proseDate';
import { buildCsv } from './csv';

// ---------------------------------------------------------------------
// Wire shapes
// ---------------------------------------------------------------------

export type BillingReadiness =
  | 'ready'
  // 0133: eligible but not yet human-confirmed — the gold awaiting state.
  | 'needs_your_ok'
  | 'needs_detail'
  | 'still_reading'
  | 'failed';

export interface PrebillingRowWire {
  note_id: string;
  occurred_at: string; // 'YYYY-MM-DD'
  hours: string | null; // numeric(6,2) wire string
  rate_band: string | null;
  after_hours: boolean;
  /** The stored summary: the editable text, never prefixed. */
  invoice_summary: string | null;
  /** The line as the invoice prints it, rendered on the server (a
   *  0-hour line opens with "No charge."); null when there is no line. */
  invoice_line: string | null;
  invoice_summary_skip_reason: string | null;
  /** Office payload only. */
  status?: string;
  readiness: BillingReadiness;
  /** Office payload only. */
  author_display_name?: string | null;
  author_user_id: string;
  // 0133 human-confirmed Ready gate.
  /** Office payload only. */
  billing_ready_at?: string | null;
  billing_ready_by_display_name: string | null;
  /** True when the person reading marked this note Ready; the server
   *  decides it, and no id rides. Optional so an older-shaped answer
   *  reads as false. */
  billing_ready_by_viewer?: boolean;
  /** LP-45: the note's pay period has closed, the server's word. Optional
   *  so an older-shaped answer reads as open. */
  pay_period_closed?: boolean;
  /** LP-45: for the person reading, closed and not the office. */
  pay_inputs_locked?: boolean;
}

interface PrebillingWire {
  month: string;
  /** The tenant's native_from boundary ('YYYY-MM-DD'), or null when native
   *  billing is unset (ships-dark default) — see PrebillingResult in
   *  api/_shared/repos/encounterNotes.ts. Office payload only. */
  native_from?: string | null;
  rows: PrebillingRowWire[];
}

export interface PrebillingNote {
  noteId: string;
  occurredAt: string;
  /** The note's hours for arithmetic: null (never entered) reads 0 here;
   *  hoursEntered tells the two apart. */
  hours: number;
  /** False when the note's hours were never entered (null on the wire). */
  hoursEntered: boolean;
  rateBand: string | null;
  afterHours: boolean;
  /** The stored summary: what the editors edit. */
  summary: string | null;
  /** The line as the invoice prints it, from the server; the summary cell
   *  and the CSV show this, never a line of the browser's own. */
  invoiceLine: string | null;
  /** True when the drafter sent the note back for more detail. */
  kickedBack: boolean;
  readiness: BillingReadiness;
  /** Resolved display name (never a uuid on screen). */
  author: string;
  /** The note's author id: the row's change doors (Edit and the Ready
   *  switch) follow canChangeNote. Never rendered. */
  authorUserId: string;
  /** 0133: when a CM confirmed Ready (ISO), else null. */
  billingReadyAt: string | null;
  /** 0133: who confirmed Ready (display name), else null. */
  billingReadyBy: string | null;
  /** True when the person reading confirmed Ready, as the server says;
   *  the Ready line then reads "Marked by you". Nothing here compares
   *  names or ids. */
  billingReadyByViewer: boolean;
  /** True when native billing is ON for the tenant and this note's
   *  service date precedes the boundary: the line exports to CSV and
   *  will never materialize a native billable_event. PER NOTE and
   *  self-clearing, a plain date fact computed regardless of readiness;
   *  the tab renders it only next to Ready, ambient (sand/neutral).
   *  The TENANT-WIDE state, native billing OFF, is not a row fact: the
   *  tab derives it from the wire's native_from (null) and renders it
   *  once, loudly. */
  beforeNativeStart: boolean;
  /** LP-45: the pay period holding the note's date has closed, as the
   *  server says: Remove is not offered. */
  payPeriodClosed: boolean;
  /** LP-45: for the person reading, the period has closed and they are
   *  not the office: the edit box holds the four facts as they are. */
  payInputsLocked: boolean;
}

export function toNote(row: PrebillingRowWire, nativeFrom: string | null): PrebillingNote {
  return {
    noteId: row.note_id,
    occurredAt: row.occurred_at,
    hours: row.hours === null ? 0 : Number(row.hours),
    hoursEntered: row.hours !== null,
    rateBand: row.rate_band,
    afterHours: row.after_hours,
    summary: row.invoice_summary,
    invoiceLine: row.invoice_line,
    kickedBack: row.invoice_summary_skip_reason === 'insufficient_content',
    readiness: row.readiness,
    author: row.author_display_name ?? 'Unknown',
    authorUserId: row.author_user_id,
    billingReadyAt: row.billing_ready_at ?? null,
    billingReadyBy: row.billing_ready_by_display_name,
    billingReadyByViewer: row.billing_ready_by_viewer === true,
    beforeNativeStart: nativeBillingGate(nativeFrom, row.occurred_at) === 'before_native_start',
    payPeriodClosed: row.pay_period_closed === true,
    payInputsLocked: row.pay_inputs_locked === true,
  };
}

// ---------------------------------------------------------------------
// Fetch + hook (clientRecordApi.ts pattern)
// ---------------------------------------------------------------------

export function prebillingKey(
  slug: string,
  clientId: string,
  month: string,
): readonly unknown[] {
  return ['prebilling', slug, clientId, month] as const;
}

interface PrebillingFetch {
  rows: PrebillingNote[];
  /** The tenant's native_from boundary, unchanged from the wire — carried
   *  alongside so the tab can render the boundary DATE in the marker's
   *  explanation ("before native billing start (July 10, 2026)") without
   *  re-deriving it per row. */
  nativeFrom: string | null;
}

/** ONE client's month. The Pre-billing tab reads it through usePrebilling;
 *  the all-clients billing file (billingBatch.ts exportMonthSequentially)
 *  reads it once per client, through this same per-client route. */
export async function fetchPrebilling(
  slug: string,
  clientId: string,
  month: string,
  signal?: AbortSignal,
): Promise<PrebillingFetch> {
  const path =
    `/api/t/${encodeURIComponent(slug)}/v1/clients/${encodeURIComponent(clientId)}` +
    `/prebilling?month=${encodeURIComponent(month)}`;
  const res = await fetch(path, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    signal,
  });
  if (!res.ok) {
    let body: unknown;
    try {
      body = await res.json();
    } catch {
      body = await res.text().catch(() => null);
    }
    throw new ApiError(res.status, body, `GET ${path} → HTTP ${res.status}`);
  }
  const payload = (await res.json()) as PrebillingWire;
  // `?? null`: the care-manager payload omits native_from. The tab never
  // reads it for that seat (the banner and the export-only marker are the
  // office's); for the office an explicit null means native billing is off.
  const nativeFrom = payload.native_from ?? null;
  return { rows: payload.rows.map((r) => toNote(r, nativeFrom)), nativeFrom };
}

export interface UsePrebillingResult {
  rows: PrebillingNote[] | null;
  /** The tenant's native_from boundary ('YYYY-MM-DD'), or null when
   *  native billing is unset for this tenant. */
  nativeFrom: string | null;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => void;
}

/** The month's billing review rows for one client. */
export function usePrebilling(
  slug: string,
  clientId: string,
  month: string,
  // Design Pass 2 (A5): the record hero's billing fact shares this hook
  // (and its query key — one fetch per month per client with the tab),
  // gated by the tab's own capability pair (view_invoice or
  // view_prebilling) via `enabled`.
  opts: { enabled?: boolean } = {},
): UsePrebillingResult {
  const query = useQuery<PrebillingFetch>({
    queryKey: prebillingKey(slug, clientId, month),
    queryFn: ({ signal }) => fetchPrebilling(slug, clientId, month, signal),
    staleTime: 30_000,
    enabled: (opts.enabled ?? true) && slug !== '' && clientId !== '' && month !== '',
  });
  return {
    rows: query.data?.rows ?? null,
    nativeFrom: query.data?.nativeFrom ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: () => {
      void query.refetch();
    },
  };
}

// ---------------------------------------------------------------------
// Mutations — inline billing edits + the human Mark-as-ready gate (0133)
// ---------------------------------------------------------------------

async function sendNoteJson(
  method: 'PATCH' | 'POST',
  slug: string,
  path: string,
  body: Record<string, unknown>,
): Promise<unknown> {
  const full = `/api/t/${encodeURIComponent(slug)}/v1${path}`;
  const res = await writeFetch(full, {
    method,
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let errBody: unknown;
    try {
      errBody = await res.json();
    } catch {
      errBody = await res.text().catch(() => null);
    }
    throw new ApiError(res.status, errBody, `${method} ${full} → HTTP ${res.status}`);
  }
  return res.json();
}

/** The billing projection fields inline-editable on a pre-billing row.
 *  `hours` is a JSON NUMBER on this wire: the server's coerceNoteHours
 *  (validators/encounterNotes.ts) rejects strings with a 400 — the
 *  pre-polish editor sent String(n) and every hours edit silently
 *  failed (day-1 polish fix). */
export interface BillingPatch {
  rate_band?: string | null;
  hours?: number;
  after_hours?: boolean;
  occurred_at?: string; // 'YYYY-MM-DD'
}

/** PATCH the billing projection (rate_band / hours / after_hours /
 *  occurred_at). Editing un-readies the note server-side, except an
 *  office edit of a note whose charge is on a draft invoice, which keeps
 *  it Ready and answers `billable_event` (markReadyOutcomeOf reads it). */
export function patchNoteBilling(
  slug: string,
  noteId: string,
  patch: BillingPatch,
): Promise<unknown> {
  return sendNoteJson('PATCH', slug, `/notes/${encodeURIComponent(noteId)}/billing`, {
    ...patch,
  });
}

/** PATCH the invoice line (also un-readies the note server-side). */
export function patchNoteSummary(
  slug: string,
  noteId: string,
  invoiceSummary: string,
): Promise<unknown> {
  return sendNoteJson('PATCH', slug, `/notes/${encodeURIComponent(noteId)}/summary`, {
    invoice_summary_draft: invoiceSummary,
  });
}

/** POST the human Mark-as-ready gate: true confirms, false undoes. The
 *  response carries `billable_event` (markReadyOutcomeOf reads it). */
export function setNoteBillingReady(
  slug: string,
  noteId: string,
  ready: boolean,
): Promise<unknown> {
  return sendNoteJson('POST', slug, `/notes/${encodeURIComponent(noteId)}/billing-ready`, {
    ready,
  });
}

// ---------------------------------------------------------------------
// Native billing: the tenant gate, mirrored client-side
// ---------------------------------------------------------------------

/**
 * What the mark-ready arm did, as the server reports it
 * (SetNoteBillingReadyResult.billable_event in
 * api/_shared/repos/encounterNotes.ts):
 *   'created'              the note's billable event is on file;
 *   'native_billing_off'   no event, and none will come from this
 *                          workspace until an owner turns native billing
 *                          on: TENANT-WIDE, the loud state;
 *   'before_native_start'  no event because this note predates the
 *                          boundary: per note, ambient, export-only;
 *   'removed' / 'none'     the unmark's answers.
 * Ready alone says nothing about revenue. This does.
 */
export type NativeBillableEventOutcome =
  | 'created'
  | 'native_billing_off'
  | 'before_native_start'
  | 'removed'
  | 'none';

const OUTCOMES: ReadonlySet<string> = new Set([
  'created',
  'native_billing_off',
  'before_native_start',
  'removed',
  'none',
]);

/** The billing-ready response's fact, or null when the body carries
 *  none (an older-shaped response): the caller falls back to the wire's
 *  native_from and the note's date, the same decision the server made. */
export function markReadyOutcomeOf(body: unknown): NativeBillableEventOutcome | null {
  if (typeof body !== 'object' || body === null) return null;
  const v = (body as Record<string, unknown>).billable_event;
  return typeof v === 'string' && OUTCOMES.has(v) ? (v as NativeBillableEventOutcome) : null;
}

/** Strict 'YYYY-MM-DD': lexicographic compare against a date works. */
const DATE_SHAPE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * The tenant's native-billing boundary from a tenants.config blob (the
 * /getTenantConfig read every page already holds). A MIRROR of the
 * server reader (api/_shared/billingConfig.ts readNativeBillingFrom),
 * same null semantics: unset OR malformed reads null, and null is the
 * tenant-wide OFF state. Trimmed before the shape check; the trim never
 * rescues garbage. Surfaces that only have the config (NotesFeed, the
 * Invoices cockpit) read it here; surfaces whose wire already carries
 * native_from (the Pre-billing tab) use the wire.
 */
export function readNativeBillingFrom(config: unknown): string | null {
  if (typeof config !== 'object' || config === null) return null;
  const billing = (config as Record<string, unknown>).billing;
  if (typeof billing !== 'object' || billing === null) return null;
  const v = (billing as Record<string, unknown>).native_from;
  if (typeof v !== 'string') return null;
  const trimmed = v.trim();
  if (!DATE_SHAPE.test(trimmed)) return null;
  return trimmed;
}

export type NativeBillingGate = 'open' | 'native_billing_off' | 'before_native_start';

/** The server's nativeBillingGate, mirrored: OFF is tenant-wide (null
 *  boundary), before-start is one note's date against a set boundary.
 *  `occurred` is 'YYYY-MM-DD' (a timestamp's first ten characters). */
export function nativeBillingGate(nativeFrom: string | null, occurred: string): NativeBillingGate {
  if (nativeFrom === null) return 'native_billing_off';
  if (occurred.slice(0, 10) < nativeFrom) return 'before_native_start';
  return 'open';
}

// ---------------------------------------------------------------------
// Pure display helpers (unit-tested with the tab)
// ---------------------------------------------------------------------

/** value -> label map over the TENANT-RESOLVED bands (#456 D2 config
 *  seam: callers pass readRateBands(tenantConfig?.config) — the
 *  NotesFeed pattern; never the static RATE_BANDS directly). */
export function bandLabelsFrom(bands: readonly RateBandOption[]): Record<string, string> {
  return Object.fromEntries(bands.map((rb) => [rb.value, rb.label]));
}

export function rateBandLabel(band: string | null, labels: Record<string, string>): string {
  if (band === null || band === '') return '';
  return labels[band] ?? band;
}

/** The export-only chip's explanation. AMBIENT on the chip in both
 *  states: this line downloads in the CSV regardless, so nothing here is
 *  a refusal; it names WHY the same line will not show up as an invoice.
 *  The OFF state's loudness lives in the tab's banner, once, not on
 *  every row. Never says the setting is missing: the reader also nulls a
 *  malformed value. */
export function nativeExportOnlyExplanation(nativeFrom: string | null): string {
  return nativeFrom === null
    ? 'Export only · native billing is not turned on for this workspace. This line downloads in the CSV but will not appear on an invoice until an owner turns native billing on.'
    : `Export only · before native billing start (${proseDate(nativeFrom)}). This line downloads in the CSV but will not appear on an invoice.`;
}

/** The ONE tenant-wide banner, shared by the Pre-billing tab and the
 *  Invoices cockpit: native billing is OFF for this workspace, so Ready
 *  produces no invoice. Pepper-deep register (something needs an owner;
 *  nothing has been sent wrong yet, so not danger). */
export const NATIVE_BILLING_OFF_BANNER =
  'Native billing is not turned on for this workspace. Notes marked ready will not become invoices; they bill by CSV export until an owner turns native billing on.';

/** The per-note loud sentence after a mark that produced no event because
 *  native billing is OFF (NotesFeed's ready line, the tab's value line). */
export const MARKED_READY_NATIVE_OFF =
  'Marked ready, but native billing is not turned on for this workspace, so this note will not become an invoice. It bills by CSV export until an owner turns native billing on.';

/** The ambient per-note sentence for a Ready note dated before a SET
 *  boundary. Names the date when the caller has it. */
export function beforeNativeStartSentence(nativeFrom: string | null): string {
  return nativeFrom === null
    ? 'Dated before native billing started, so it bills by CSV export rather than an invoice.'
    : `Dated before native billing started (${proseDate(nativeFrom)}), so it bills by CSV export rather than an invoice.`;
}

/** What the hours became when no invoice is coming: they still ride the
 *  month's CSV export. A 0h note keeps its no-charge fact. */
export function exportHoursSentence(hours: number, month: string): string {
  return hours > 0
    ? `${formatHours(hours)} captured for ${monthName(month)}'s CSV export.`
    : 'No charge for this one.';
}

/** The no_billable_events refusal, explained (founder feedback
 *  2026-07-13; shared across compile / approve / recompile per the #528
 *  follow-on). The server carries `pre_window_ready_count` + `native_from`
 *  ONLY when pre-window Ready notes explain the zero; returns null
 *  otherwise so each surface keeps its own plain copy. COPY PRECISION
 *  (#528 follow-on): states what is true NOW and never promises the lines
 *  will invoice once the window covers them — pre-window Ready notes skip
 *  the rate-on-file check (createNativeBillableEvent returns early), so
 *  some may ALSO lack a rate. */
export function noBillableEventsExplanation(body: unknown): string | null {
  const detail = (body ?? {}) as { pre_window_ready_count?: number; native_from?: string | null };
  const count = detail.pre_window_ready_count;
  if (typeof count !== 'number' || count <= 0) return null;
  const subject = count === 1 ? '1 ready line' : `${count} ready lines`;
  const outcome =
    count === 1
      ? 'It exports to CSV but does not invoice.'
      : 'They export to CSV but do not invoice.';
  return detail.native_from != null
    ? `${subject} in this period ${count === 1 ? 'is' : 'are'} dated before the native billing start (${proseDate(detail.native_from)}). ${outcome}`
    : count === 1
      ? "This note won't be on an invoice here yet. The office bills it the old way for now."
      : `These ${count} notes won't be on an invoice here yet. The office bills them the old way for now.`;
}

/** "1.5 hours" / "1 hour" / "0 hours" — trimmed, never "1.50". */
export function formatHours(hours: number): string {
  const trimmed = String(Number(hours.toFixed(2)));
  return trimmed === '1' ? '1 hour' : `${trimmed} hours`;
}

/** The warning a line that says no charge but carries hours shows. A
 *  warning, never a refusal. */
export function noChargeHoursWarning(hours: number): string {
  return `This line says no charge but has ${formatHours(hours)}, so it will be billed. Set the hours to 0 if it was a courtesy.`;
}

/** The warning a line typed in capitals shows (isInCapitals). */
export const CAPITALS_WARNING = 'This line is in capitals. Edit it to sentence case so it reads well on the invoice.';

/** The no-charge-with-hours warning for a note, or null. It shows when the
 *  hours were entered and are above zero, the band is not per-visit (a
 *  per-visit note cannot be set to 0 hours), and the stored summary says
 *  no charge. Readiness never matters: a Ready note warns too. */
export function noChargeHoursWarningFor(
  hours: number | null,
  rateBand: string | null,
  summary: string | null,
  bands: readonly RateBandOption[],
): string | null {
  if (hours === null || !(hours > 0)) return null;
  if (rateBand !== null && isPerVisitBand(rateBand, bands)) return null;
  return saysNoCharge(summary) ? noChargeHoursWarning(hours) : null;
}

/** 'YYYY-MM' → 'July' (the empty-state voice) */
export function monthName(month: string): string {
  const m = Number(month.slice(5, 7));
  return MONTHS[m - 1] ?? month;
}

/** 'YYYY-MM' → 'July 2026' (the picker label). */
export function monthLabel(month: string): string {
  return `${monthName(month)} ${month.slice(0, 4)}`;
}

export function prevMonth(month: string): string {
  const y = Number(month.slice(0, 4));
  const m = Number(month.slice(5, 7));
  return m === 1 ? `${y - 1}-12` : `${month.slice(0, 4)}-${String(m - 1).padStart(2, '0')}`;
}

export function nextMonth(month: string): string {
  const y = Number(month.slice(0, 4));
  const m = Number(month.slice(5, 7));
  return m === 12 ? `${y + 1}-01` : `${month.slice(0, 4)}-${String(m + 1).padStart(2, '0')}`;
}

/** The rows the billing CSV includes: HUMAN-CONFIRMED READY only (0133 —
 *  a care manager set it to Ready). Eligible-but-unconfirmed
 *  (needs_your_ok) stays on screen but OFF the export until confirmed;
 *  the human gate holds at the export boundary. A confirmed 0h row IS
 *  included (it shows on the invoice that the work was done at no
 *  charge). */
export function readyRowsOf(rows: readonly PrebillingNote[]): PrebillingNote[] {
  return rows.filter((r) => r.readiness === 'ready');
}

/** Total READY hours per rate band, in tenant-band order (footer). */
export function readyHoursByBand(
  rows: readonly PrebillingNote[],
  bands: readonly RateBandOption[],
): { band: string; label: string; hours: number }[] {
  const labels = bandLabelsFrom(bands);
  const totals = new Map<string, number>();
  for (const r of readyRowsOf(rows)) {
    const band = r.rateBand ?? '';
    totals.set(band, (totals.get(band) ?? 0) + r.hours);
  }
  const ordered = [
    ...bands.map((rb) => rb.value).filter((b) => totals.has(b)),
    ...[...totals.keys()].filter((b) => !bands.some((rb) => rb.value === b)),
  ];
  return ordered.map((band) => ({
    band,
    label: rateBandLabel(band, labels) || 'No rate band',
    hours: Number((totals.get(band) ?? 0).toFixed(2)),
  }));
}

const PREBILLING_CSV_HEADER: readonly string[] = [
  'Client',
  'Date',
  'Hours',
  'Rate band',
  'After Hours',
  'Summary',
  'Posted by',
];

/** ONE Ready row as the seven CSV fields. The per-client download and
 *  the all-clients file both serialise through here, so a column cannot
 *  drift between the two. */
function prebillingCsvRow(
  clientName: string,
  r: PrebillingNote,
  labels: Record<string, string>,
): string[] {
  return [
    clientName,
    r.occurredAt,
    String(Number(r.hours.toFixed(2))),
    rateBandLabel(r.rateBand, labels),
    r.afterHours ? 'Yes' : 'No',
    // The server's rendered line as it stands (a 0-hour line opens with
    // "No charge."): the browser renders nothing itself.
    r.invoiceLine ?? '',
    r.author,
  ];
}

/** A client's rows oldest first, for the files. The wire is newest first
 *  (visit date, then posted time, then id, each descending) and carries no
 *  posted time, so the rows are reversed and then stably sorted by date:
 *  same-day notes come out in the order they were posted. */
function oldestFirst(rows: readonly PrebillingNote[]): PrebillingNote[] {
  return [...rows]
    .reverse()
    .sort((a, b) => (a.occurredAt < b.occurredAt ? -1 : a.occurredAt > b.occurredAt ? 1 : 0));
}

/** The billing CSV over the READY rows (columns per the GC brief). */
export function buildPrebillingCsv(
  clientName: string,
  rows: readonly PrebillingNote[],
  bands: readonly RateBandOption[],
): string {
  return buildBatchPrebillingCsv([{ clientName, rows }], bands);
}

/** The billing CSV for MANY clients in one file: one header, then each
 *  client's READY rows, oldest first, in the order the clients are given. A client with
 *  no Ready rows contributes nothing. Same columns, same row rule, and
 *  the same Summary cell (the server's rendered line) as the per-client
 *  download. */
export function buildBatchPrebillingCsv(
  entries: ReadonlyArray<{ clientName: string; rows: readonly PrebillingNote[] }>,
  bands: readonly RateBandOption[],
): string {
  const labels = bandLabelsFrom(bands);
  const body = entries.flatMap((entry) =>
    oldestFirst(readyRowsOf(entry.rows)).map((r) => prebillingCsvRow(entry.clientName, r, labels)),
  );
  return buildCsv([PREBILLING_CSV_HEADER, ...body]);
}

/** 'billing-all-clients-{YYYY-MM}.csv': the month names the file, never
 *  a client. */
export function batchPrebillingCsvFilename(month: string): string {
  return `billing-all-clients-${month}.csv`;
}

/** '{client-name}-billing-{YYYY-MM}.csv' (name slugged for a filename). */
export function prebillingCsvFilename(clientName: string, month: string): string {
  const slug =
    clientName
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'client';
  return `${slug}-billing-${month}.csv`;
}
